import { STYLE_GUIDE_MAX_WORDS, countStyleGuideWords } from "../../../../../src/shared/styleGuideWordLimit";
import React, { useState, useEffect, useRef } from "react";
import { Container } from "./styles";
import Modal from "react-modal";
import { WordCount } from "../WordCount";
import usePrevious from "../../../hooks/usePrevious";
import Auth from "../../../Auth";
import { StyleTemplate } from "./styleTemplate";
import { countWords } from "../../../Utils";
import InlineSVG from "react-inlinesvg/esm";
import { LoadingButton } from "./LoadingButton";
import { TypeBot } from "./TypeBot";
import { Compressing } from "./Compressing";
import SendToDoc from "../SendToDoc";
import TextButton from "../../TextButton";
import { ResetConfirmation } from "./ResetConfirmation";
import { AppEventPublisher, StoryBibleType, StoryBibleAction } from "../../../AppEventPublisher.js";
import { StyleInstructionsEditor } from "../../StyleGuide/StyleInstructionsEditor";

Modal.setAppElement("#root");

const customStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(55, 55, 55, 0.7)",
    zIndex: "100000001",
  },
  content: {
    top: "10%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    zIndex: "20000",
    width: "750px",
    transform: "translate(-50%, 0)",
    borderRadius: "20px",
    border: "0px",
    padding: "0px",
    background: "transparent",
    boxShadow: "var(--default-drop-shadow)",
    height: "80vh",
  },
};

const stateKeys = {
  EMPTY: "EMPTY",
  HAS_USER_SAMPLE: "HAS_USER_SAMPLE",
  ANALYZING_USER_SAMPLE: "ANALYZING_USER_SAMPLE",
  EMPTY_TEMPLATE: "EMPTY_TEMPLATE",
  USER_FILLED_TEMPLATE: "USER_FILLED_TEMPLATE",
  RESET_TEMPLATE_INTERVENTION: "RESET_TEMPLATE_INTERVENTION",
  LONG_STYLE_GENERATED: "LONG_STYLE_GENERATED",
  COMPRESSING_GENERATED_STYLE: "COMPRESSING_GENERATED_STYLE",
  COMPRESSING_USER_STYLE: "COMPRESSING_USER_STYLE",
  FINISHED: "FINISHED",
};

const maxProseWords = 1000;
const maxStyleWords = 1000;

const StyleMatcher = ({ close, setError, setStyle, project, initialSample = "", variant = "default" }) => {
  const isStyleGuideVariant = variant === "styleGuide";
  const analyzeEndpoint = isStyleGuideVariant
    ? "/api/ai/storyEngine/style/analyze-detailed"
    : "/api/ai/storyEngine/style/analyze";
  const samplePlaceholder =
    "Paste in up to 1000 words of your writing. Your writing sample should match the style you want for this story.";
  const processingHeading = isStyleGuideVariant ? "Analyzing" : "Compressing";
  const finishedInstructionLimit = isStyleGuideVariant ? STYLE_GUIDE_MAX_WORDS : maxStyleWords;
  const [currentState, setCurrentState] = useState(initialSample ? stateKeys.HAS_USER_SAMPLE : stateKeys.EMPTY);
  const [textAreaVal, setTextAreaVal] = useState(initialSample);
  const [writingSample, setWritingSample] = useState(initialSample);
  const [generatedStyleName, setGeneratedStyleName] = useState("");
  const [isSavingStyle, setIsSavingStyle] = useState(false);
  const [localError, setLocalError] = useState("");
  const reportError = (message) => {
    setLocalError(message);
    setError?.(message);
  };
  const states = {
    EMPTY: {
      subheading: "Step 1:",
      mainHeading: "Paste in your writing sample",
      textAreaPlaceholder: samplePlaceholder,
      actionButtons: [
        {
          component: "button",
          className: "secondary",
          label: "Skip",
          onClick: () => {
            setCurrentState(stateKeys.EMPTY_TEMPLATE);
          },
        },
        {
          component: "button",
          className: "main",
          label: "Next",
          disabled: true,
        },
      ],
      wordCount: (
        <WordCount
          textContent={textAreaVal}
          maxLength={maxProseWords}
        />
      ),
    },
    HAS_USER_SAMPLE: {
      subheading: "Step 1:",
      mainHeading: "Paste in your writing sample",
      textAreaPlaceholder: samplePlaceholder,
      actionButtons: [
        {
          component: "button",
          className: "secondary",
          label: "Skip",
          onClick: () => setCurrentState(stateKeys.EMPTY_TEMPLATE),
        },
        {
          className: "main",
          component: "button",
          label: "Next",
          onClick: () => {
            AppEventPublisher.storyBible.action(StoryBibleType.style, StoryBibleAction.SubmitStyleExample);
            setWritingSample(textAreaVal);
            setCurrentState(stateKeys.COMPRESSING_GENERATED_STYLE);
          },
          disabled: countWords(textAreaVal) > maxProseWords,
        },
      ],
      wordCount: (
        <WordCount
          textContent={textAreaVal}
          maxLength={maxProseWords}
          highlightOverage={true}
        />
      ),
    },
    ANALYZING_USER_SAMPLE: {
      subheading: "Step 1:",
      mainHeading: "Paste in your writing sample",
      transitionComponent: <TypeBot />,
      actionButtons: [
        {
          label: "Next",
          className: "main",
          component: LoadingButton,
          onClick: () => setCurrentState(stateKeys.COMPRESSING_USER_STYLE),
        },
      ],
    },
    EMPTY_TEMPLATE: {
      subheading: "Step 2:",
      mainHeading: "Fill in the template",
      instruction: "The information you fill in will be condensed into your final style",
      textAreaPlaceholder: "Follow the template provided. You can click Reset to get a new template.",
      actionButtons: [
        {
          label: "Send to Doc",
          className: "secondary",
          component: SendToDoc,
          project: project,
          content: textAreaVal,
          title: "Writing Sample Style",
        },
      ],
    },
    USER_FILLED_TEMPLATE: {
      subheading: "Step 2:",
      mainHeading: "Fill in the template",
      instruction: "The information you fill in will be condensed into your final style",
      headerButtons: [
        {
          label: "Reset",
          className: "reset",
          component: TextButton,
          src: "/assets/restart.svg",
          iconSize: 18,
          onClick: () => setCurrentState(stateKeys.RESET_TEMPLATE_INTERVENTION),
        },
      ],
      textAreaPlaceholder: "Follow the template provided. You can click Reset to get a new template.",
      actionButtons: [
        {
          label: "Send to Doc",
          className: "secondary",
          component: SendToDoc,
          project: project,
          content: textAreaVal,
          title: "Writing Sample Style",
        },
        {
          label: "Next",
          className: "main",
          component: "button",
          onClick: () => setCurrentState(stateKeys.COMPRESSING_USER_STYLE),
          disabled: countWords(textAreaVal) > maxProseWords,
        },
      ],
      wordCount: (
        <WordCount
          textContent={textAreaVal}
          maxLength={maxProseWords}
          highlightOverage={true}
        />
      ),
    },
    RESET_TEMPLATE_INTERVENTION: {
      subheading: "Step 2:",
      mainHeading: "Fill in the template",
      instruction: "The information you fill in will be condensed into your final style",

      textAreaPlaceholder: "Follow the template provided. You can click Reset to get a new template.",
      actionButtons: [
        {
          label: "Send to Doc",
          className: "secondary",
          component: SendToDoc,
          project: project,
          content: textAreaVal,
          title: "Writing Sample Style",
        },
        {
          label: "Next",
          className: "main",
          component: "button",
          onClick: () => setCurrentState(stateKeys.COMPRESSING_USER_STYLE),
        },
      ],
      modal: (
        <ResetConfirmation
          back={() => setCurrentState(stateKeys.USER_FILLED_TEMPLATE)}
          confirm={() => setCurrentState(stateKeys.EMPTY_TEMPLATE)}
        />
      ),
    },
    LONG_STYLE_GENERATED: {
      subheading: "Step 2:",
      mainHeading: "Correct & Improve",
      instruction: "Clean up anything the AI got wrong about your style, or that you don't think is needed. This will be condensed in the final style.",
      textAreaPlaceholder: "Follow the template provided. You can click Reset to get a new template.",
      actionButtons: [
        {
          label: "Send to Doc",
          className: "secondary",
          component: SendToDoc,
          project: project,
          content: textAreaVal,
          title: "Writing Sample Style",
        },
        {
          label: "Next",
          className: "main",
          component: "button",
          onClick: () => setCurrentState(stateKeys.COMPRESSING_GENERATED_STYLE),
          disabled: countWords(textAreaVal) > maxProseWords,
        },
      ],
      wordCount: (
        <WordCount
          textContent={textAreaVal}
          maxLength={maxProseWords}
          highlightOverage={true}
        />
      ),
    },
    COMPRESSING_GENERATED_STYLE: {
      subheading: "Step 2:",
      mainHeading: processingHeading,

      transitionComponent: <Compressing />,
    },
    COMPRESSING_USER_STYLE: {
      subheading: "Step 2:",
      mainHeading: "Fill in the template",
      instruction: "The information you fill in will be condensed into your final style",
      transitionComponent: <Compressing />,
    },
    FINISHED: {
      mainHeading: "Done!",
      textAreaPlaceholder: "The text in this box will replace your existing style.",
      actionButtons: [
        {
          label: "Insert",
          className: "main",
          component: "button",
          onClick: async () => {
            setIsSavingStyle(true);
            try {
              setLocalError("");
              AppEventPublisher.storyBible.action(StoryBibleType.style, StoryBibleAction.SelectMyStyle);
              await setStyle(textAreaVal, {
                writingSample,
                ...(isStyleGuideVariant
                  ? { name: generatedStyleName || "Generated Style" }
                  : {}),
              });
              close();
            } catch {
              reportError("There was an error saving your style. Please try again.");
            } finally {
              setIsSavingStyle(false);
            }
          },
          disabled: isSavingStyle || (finishedInstructionLimit != null && (isStyleGuideVariant ? countStyleGuideWords(textAreaVal) : countWords(textAreaVal)) > finishedInstructionLimit),
        },
      ],
      wordCount: finishedInstructionLimit != null ? (
        <WordCount
          textContent={textAreaVal}
          maxLength={finishedInstructionLimit}
          wordCount={isStyleGuideVariant ? countStyleGuideWords(textAreaVal) : undefined}
          highlightOverage={true}
        />
      ) : null,
    },
  };
  const prevState = usePrevious(currentState);
  const contentRef = useRef();

  const analyzeSample = async (prevState) => {
    try {
      setLocalError("");
      const res = await Auth.fetchWithIdToken(analyzeEndpoint, {
        sample: textAreaVal,
      });
      if (res.text) {
        if (isStyleGuideVariant) {
          setGeneratedStyleName(
            typeof res.name === "string" && res.name.trim()
              ? res.name.trim()
              : "Generated Style",
          );
        }
        setCurrentState(stateKeys.FINISHED);
        setTextAreaVal(res.text);
      }
    } catch (e) {
      reportError("There was an error analyzing your text. Please try again.");
      setCurrentState(prevState);
    }
  };

  const compressStyle = async (prevState) => {
    try {
      setLocalError("");
      const res = await Auth.fetchWithIdToken("/api/ai/storyEngine/style/compress", {
        style: textAreaVal,
      });
      if (res.text) {
        setCurrentState(stateKeys.FINISHED);
        setTextAreaVal(res.text);
      }
    } catch (e) {
      reportError("There was an error compressing your text. Please try again.");
      setCurrentState(prevState);
    }
  };

  useEffect(() => {
    const transition = `${prevState}--${currentState}`;
    switch (transition) {
      case `${stateKeys.HAS_USER_SAMPLE}--${stateKeys.ANALYZING_USER_SAMPLE}`:
        analyzeSample(prevState);
        break;
      case `${stateKeys.EMPTY}--${stateKeys.EMPTY_TEMPLATE}`:
      case `${stateKeys.HAS_USER_SAMPLE}--${stateKeys.EMPTY_TEMPLATE}`:
      case `${stateKeys.USER_FILLED_TEMPLATE}--${stateKeys.EMPTY_TEMPLATE}`:
      case `${stateKeys.RESET_TEMPLATE_INTERVENTION}--${stateKeys.EMPTY_TEMPLATE}`:
        setTextAreaVal(StyleTemplate);
        break;
      case `${stateKeys.HAS_USER_SAMPLE}--${stateKeys.COMPRESSING_GENERATED_STYLE}`:
        analyzeSample(prevState);
        break;
      case `${stateKeys.USER_FILLED_TEMPLATE}--${stateKeys.COMPRESSING_USER_STYLE}`:
      case `${stateKeys.RESET_TEMPLATE_INTERVENTION}--${stateKeys.COMPRESSING_USER_STYLE}`:
        if (isStyleGuideVariant) {
          analyzeSample(prevState);
        } else {
          compressStyle(prevState);
        }
        break;
    }
  }, [currentState]);

  useEffect(() => {
    if (currentState === stateKeys.EMPTY && textAreaVal) {
      setCurrentState(stateKeys.HAS_USER_SAMPLE);
    }
    if (currentState === stateKeys.HAS_USER_SAMPLE && !textAreaVal) {
      setCurrentState(stateKeys.EMPTY);
    }
    if (currentState === stateKeys.EMPTY_TEMPLATE && textAreaVal !== StyleTemplate) {
      setCurrentState(stateKeys.USER_FILLED_TEMPLATE);
    }
  }, [textAreaVal]);

  return (
    <Modal
      isOpen={true}
      contentRef={(node) => (contentRef.current = node)}
      style={customStyles}
    >
      <Container>
        <div className="header">
          <div className="headings">
            <div className="sub-heading">{states[currentState].subheading}</div>
            <h2 className="main-heading">{states[currentState].mainHeading}</h2>
            <p className="instruction">{states[currentState].instruction}</p>
          </div>
          <div className="header-buttons">
            {states[currentState].headerButtons?.map((props) => (
              <props.component {...props}>{props.label}</props.component>
            ))}
          </div>
          <div
            aria-label="Close Match My Style"
            className="close"
            onClick={close}
          >
            <InlineSVG src="/assets/brainstorm/close-modal.svg" />
          </div>
        </div>
        <div className="content">
          {states[currentState].textAreaPlaceholder &&
            (isStyleGuideVariant && currentState === stateKeys.FINISHED ? (
              <div className="finished-style-preview">
                <StyleInstructionsEditor
                  value={textAreaVal}
                  onChange={setTextAreaVal}
                  onBlur={setTextAreaVal}
                />
              </div>
            ) : (
              <textarea
                className="scrollable"
                placeholder={states[currentState].textAreaPlaceholder}
                value={textAreaVal}
                onChange={(e) => {
                  let val = e.target.value;

                  setTextAreaVal(val);
                }}
              />
            ))}
          {states[currentState].transitionComponent}
        </div>

        <div className="footer">
          <div className="wordCount">{states[currentState].wordCount}</div>
          <div className="actions">
            {states[currentState].actionButtons?.map((props) => (
              <props.component {...props}>{props.label}</props.component>
            ))}
          </div>
        </div>
        {localError && (
          <p className="error" role="status">
            {localError}
          </p>
        )}
        {states[currentState].modal}
      </Container>
    </Modal>
  );
};

export default StyleMatcher;
